import { helpers, mui, React, ts, ui, useNavigate, useSelector } from '_core';

import * as utils from './backtest-set-utils';

type BacktestSetFormProps = {
  open: boolean;
  setOpen: (_open: boolean) => void;
};

const BacktestSetForm = ({ open, setOpen }: BacktestSetFormProps) => {
  const backtestSets = useSelector((state) => state.resources.backtest_sets as ts.types.backtestSet.BacktestSet[]);
  const workspaces = useSelector((state) => state.resources.workspaces as ts.types.workspace.Workspace[]);
  const currentWorkspaceId = useSelector((state) => state.ui.currentWorkspaceId);
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = React.useState('');
  const [name, setName] = React.useState('');
  const [handle, setHandle] = React.useState('');
  const [template, setTemplate] = React.useState<ts.types.backtestSet.BacktestSet>();
  const [loadingCreate, setLoadingCreate] = React.useState(false);
  const [workspace, setWorkspace] = React.useState(workspaces.find((w) => w.id == currentWorkspaceId) || workspaces[0]);

  // Create handle while typing name
  React.useEffect(() => {
    setHandle(helpers.createHandle(name, 'bts'));
  }, [name]);

  const handleSubmit = async () => {
    setErrorMessage('');

    // Validation
    if (!name || !handle) {
      setErrorMessage('All attributes are required.');
      setLoadingCreate(false);
      return;
    }

    setLoadingCreate(true);

    const newBacktestSet = utils.definition.getNewBacktestSet(name, handle, template);
    utils.apiProcedures.createBacktestSet(
      newBacktestSet,
      async (data) => {
        if (workspace)
          await helpers.resourcesUtils.addToWorkspace(data.id, ts.enums.UI_RESOURCE_TYPE_ENUM.BACKTEST_SET, workspace);
        setOpen(false);
        navigate(`/backtest_sets/${data.id}/definition`);
      },
      async (err) => {
        setErrorMessage(err);
        setLoadingCreate(false);
      }
    );
  };

  const formContent = () => (
    <mui.core.Box mt={3}>
      <mui.core.Grid container spacing={3}>
        <mui.core.Grid size={{ xs: 12, sm: 6 }}>
          <mui.core.TextField
            autoFocus
            id="name"
            label="Backtest Set name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            variant="outlined"
            required
            size="small"
            inputProps={{
              autocomplete: 'off',
            }}
          />
        </mui.core.Grid>
        <mui.core.Grid size={{ xs: 12, sm: 6 }}>
          <mui.core.TextField
            id="handle"
            label="Backtest Set handle"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            fullWidth
            variant="outlined"
            required
            size="small"
          />
        </mui.core.Grid>
      </mui.core.Grid>
      <mui.core.Box py={4}>
        <ui.ResourceAutocomplete<ts.types.common.ResourceExpanded>
          enableNone
          value={template?.id}
          setValue={(id) => setTemplate(backtestSets.find((u) => u.id == id))}
          type={ts.enums.RESOURCES_TYPES_ENUM.BACKTEST_SET}
          filter={(bs) => !bs.source_resource_id}
          inputProps={{
            fullWidth: true,
            label: 'Template',
            variant: 'outlined',
            required: true,
            size: 'small',
          }}
        />
      </mui.core.Box>
      <ui.WorkspaceSelector value={workspace} setValue={setWorkspace} />
    </mui.core.Box>
  );

  return (
    <mui.core.Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      {errorMessage && (
        <mui.core.Alert onClose={() => setErrorMessage('')} severity="error">
          {errorMessage}
        </mui.core.Alert>
      )}
      <ui.DialogTitle closeAction={() => setOpen(false)}>Create Backtest Set</ui.DialogTitle>
      <mui.core.DialogContent>{formContent()}</mui.core.DialogContent>
      <mui.core.DialogActions>
        <ui.Button
          variant="contained"
          color="primary"
          loading={loadingCreate}
          onClick={handleSubmit}
          fullWidth
          style={{ margin: '0rem 1rem 1rem' }}
        >
          Save Backtest Set
        </ui.Button>
      </mui.core.DialogActions>
    </mui.core.Dialog>
  );
};

export default BacktestSetForm;
